import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Heading,
  Input,
  PageLoader,
  Paragraph,
  Sheet,
  SheetContent,
} from '@/components/atoms';
import { FormField } from '@/components/molecules';
import { useWhatsAppTemplates } from '@/api';
import {
  applyCustomerNameToTemplateVariables,
  buildTemplateVariableDefaults,
  getTemplateVariableNames,
} from '@/lib/whatsapp-templates/template-variables';
import { TemplateForm } from '../templates/components/template-form';
import { TemplatePreview } from '../templates/components/template-preview';
import { TemplateStatusBadge } from '../templates/components/template-status-badge';
import type { WhatsAppTemplate } from '@/types/whatsapp-template';
import { ArrowLeft, LayoutTemplate, Plus, Search, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

type TemplateSheetView = 'list' | 'send' | 'create';

interface TemplateSendSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationName?: string;
  onSend: (payload: {
    templateId: number;
    templateTitle: string;
    previewBody: string;
    templateVariables: Record<string, string>;
    variableNames: string[];
  }) => void;
  isSending?: boolean;
}

function buildDefaultVariables(
  template: WhatsAppTemplate,
  conversationName?: string
) {
  return buildTemplateVariableDefaults(template, conversationName);
}

export const TemplateSendSheet = ({
  open,
  onOpenChange,
  conversationName,
  onSend,
  isSending = false,
}: TemplateSendSheetProps) => {
  const [view, setView] = useState<TemplateSheetView>('list');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});

  const { data, isLoading, refetch } = useWhatsAppTemplates({
    status: 'APPROVED',
    limit: 50,
    search: search.trim() || undefined,
  });

  const templates = useMemo(
    () => (data?.templates ?? []).filter((t) => t.status === 'approved'),
    [data?.templates]
  );

  const variableNames = useMemo(() => {
    if (!selected) return [];
    return getTemplateVariableNames(selected);
  }, [selected]);

  useEffect(() => {
    if (!open) {
      setView('list');
      setSelected(null);
      setSearch('');
      setVariables({});
    }
  }, [open]);

  useEffect(() => {
    if (!selected) return;
    setVariables(buildDefaultVariables(selected, conversationName));
  }, [selected, conversationName]);

  const canSend =
    !!selected &&
    variableNames.every((name) => variables[name]?.trim().length > 0);

  const handleSend = () => {
    if (!selected || !canSend) return;

    const trimmed = Object.fromEntries(
      Object.entries(variables).map(([key, value]) => [key, value.trim()])
    );
    const templateVariables = applyCustomerNameToTemplateVariables(
      variableNames,
      trimmed,
      conversationName
    );

    onSend({
      templateId: selected.id,
      templateTitle: selected.title,
      previewBody: selected.body,
      templateVariables,
      variableNames,
    });
  };

  const goToList = () => {
    setView('list');
    setSelected(null);
  };

  const headerTitle =
    view === 'create'
      ? 'Create template'
      : view === 'send' && selected
        ? selected.title
        : 'WhatsApp templates';

  const headerDescription =
    view === 'create'
      ? 'Submit a new template for Meta approval'
      : view === 'send'
        ? 'Fill in variables and send an approved template'
        : 'Meta-approved · ready to send in one tap';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col gap-0 overflow-hidden p-0',
          view === 'create'
            ? 'sm:max-w-2xl md:max-w-3xl'
            : 'sm:max-w-xl md:max-w-2xl'
        )}
      >
        <div className="shrink-0 border-b border-accent px-4 py-4 md:px-6">
          <div className="flex items-start gap-3">
            {view !== 'list' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-0.5 size-8 shrink-0 p-0"
                onClick={goToList}
                aria-label="Back to templates"
              >
                <ArrowLeft className="size-4" />
              </Button>
            ) : (
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <LayoutTemplate className="size-4" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <Heading size="lg" className="text-lg md:text-xl">
                {headerTitle}
              </Heading>
              <Paragraph size="sm" className="text-muted-foreground">
                {headerDescription}
              </Paragraph>
            </div>
            {view === 'list' && (
              <Button
                type="button"
                size="sm"
                className="shrink-0"
                onClick={() => setView('create')}
              >
                <Plus className="size-4" />
                <span className="hidden sm:inline">Create new</span>
                <span className="sm:hidden">New</span>
              </Button>
            )}
          </div>
        </div>

        {view === 'list' && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-accent px-4 py-3 md:px-6">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 md:px-6">
              {isLoading ? (
                <PageLoader message="Loading templates…" className="py-12" />
              ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                  <LayoutTemplate className="size-10 text-muted-foreground/50" />
                  <Paragraph className="font-medium">
                    No approved templates
                  </Paragraph>
                  <Paragraph
                    size="sm"
                    className="text-muted-foreground max-w-xs"
                  >
                    Create a template and wait for Meta approval before sending
                    outside the 24h window.
                  </Paragraph>
                  <Button type="button" onClick={() => setView('create')}>
                    <Plus className="size-4" />
                    Create new template
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => {
                        setSelected(template);
                        setView('send');
                      }}
                      className={cn(
                        'rounded-xl border border-accent bg-white p-4 text-left transition-colors',
                        'hover:border-primary/30 hover:bg-primary/5'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm md:text-base truncate">
                            {template.title}
                          </p>
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground md:text-sm">
                            {template.body}
                          </p>
                        </div>
                        <TemplateStatusBadge status={template.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>{template.category}</span>
                        {template.isAiCompatible ? (
                          <span className="text-primary">AI compatible</span>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'create' && (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 md:px-6">
            <TemplateForm
              compact
              onSuccess={() => {
                void refetch();
                goToList();
              }}
            />
          </div>
        )}

        {view === 'send' && selected && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="flex flex-col gap-6 px-4 py-4 md:px-6 md:py-6">
              {variableNames.length > 0 && (
                <div className="space-y-3">
                  <Paragraph size="sm" className="font-semibold">
                    Template variables
                  </Paragraph>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {variableNames.map((name) => (
                      <FormField key={name} label={name.replace(/_/g, ' ')}>
                        <Input
                          value={variables[name] ?? ''}
                          onChange={(e) =>
                            setVariables((current) => ({
                              ...current,
                              [name]: e.target.value,
                            }))
                          }
                          placeholder={`Enter ${name.replace(/_/g, ' ')}`}
                        />
                      </FormField>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Paragraph size="sm" className="mb-3 font-semibold">
                  Live preview
                </Paragraph>
                <TemplatePreview
                  header={selected.header}
                  body={selected.body}
                  footer={selected.footer}
                  buttons={selected.buttons}
                  variables={selected.variables}
                  variableValues={variables}
                />
                <Paragraph size="sm" className="mt-3 text-muted-foreground">
                  Uses approved WhatsApp template — compliant outside the 24h
                  window.
                </Paragraph>
              </div>
            </div>

            <div className="sticky bottom-0 mt-auto border-t border-accent bg-white px-4 py-4 md:px-6">
              <Button
                type="button"
                className="w-full"
                size="lg"
                disabled={!canSend || isSending}
                isLoading={isSending}
                onClick={handleSend}
              >
                <Send className="size-4" />
                Send template
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
